/**
 * LCD Survey Service Layer
 * Calls the Node backend (/lcd-survey/*) for eligibility checks and survey submission.
 */

import { nodeApi } from "@shared/api/apiClient";

class LcdSurveyService {
  /**
   * Is the (salesgroup, dealercode) pair an adopted LCD in BPI for current Qtr/FY?
   * Returns { success, eligible, finyear, quarter }.
   */
  async checkEligibility({ salesgroup, dealerCode, userCode }) {
    try {
      const response = await nodeApi.get("/lcd-survey/eligibility", {
        params: { salesgroup, dealercode: dealerCode, userCode },
      });
      return {
        success: true,
        eligible: !!response?.data?.eligible,
        finyear: response?.data?.finyear,
        quarter: response?.data?.quarter,
        alreadyDoneToday: !!response?.data?.alreadyDoneToday,
        previousAnswers: response?.data?.previousAnswers || null,
      };
    } catch (error) {
      console.error("LCD eligibility check failed:", error);
      // Per scope §5.4: BPI fetch fail is non-blocking — skip the survey.
      return { success: false, eligible: false, error: error?.message };
    }
  }

  /**
   * Submit the 3-field survey for a check-in (visitPlanRecordId).
   */
  async submitSurvey(payload) {
    try {
      const response = await nodeApi.post("/lcd-survey/submit", payload);
      return {
        success: true,
        data: response?.data?.data,
        message: response?.data?.message || "Survey submitted",
      };
    } catch (error) {
      console.error("LCD survey submit failed:", error);
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit survey";
      return { success: false, message: apiError };
    }
  }

  /**
   * Login-time stale-visit reminder data for the BM.
   */
  async getReminder({ userCode, salesgroup }) {
    try {
      const response = await nodeApi.get("/lcd-survey/reminder", {
        params: { userCode, salesgroup },
      });
      return {
        success: true,
        staleCount: response?.data?.staleCount ?? 0,
        dealers: response?.data?.dealers || [],
        snoozedUntil: response?.data?.snoozedUntil || null,
        thresholdDays: response?.data?.thresholdDays,
      };
    } catch (error) {
      console.error("LCD reminder fetch failed:", error);
      return { success: false, staleCount: 0, dealers: [], snoozedUntil: null };
    }
  }

  async recordReminderAction({ userCode, action }) {
    try {
      const response = await nodeApi.post("/lcd-survey/reminder/action", {
        userCode,
        action,
      });
      return { success: true, data: response?.data?.data };
    } catch (error) {
      console.error("LCD reminder action failed:", error);
      return { success: false };
    }
  }
}

export const lcdSurveyService = new LcdSurveyService();
export default lcdSurveyService;
